import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Reveal } from "@/components/Reveal";
import { api, resolveMediaUrl } from "@/lib/api";

/**
 * DirectoryChapter (copy-only variant)
 * ------------------------------------
 * The closing act of the continuous home-page scene. The photograph
 * (or video) is still the same sticky background the Hero and
 * Manifesto shared — the visitor never leaves the "film". This
 * component just paints a deep editorial wash over it so the chapter
 * grid can own the frame.
 *
 * The "Films" card previews the first Film's cover image inside the
 * card itself — a subtle editorial breadcrumb that the next chapter
 * of the studio is a continuation, not a new page.
 */

const DIRECTORY = [
  { to: "/films",        num: "I",   title: "Films",        note: "Cinematic archive — fullscreen playback" },
  { to: "/gallery",      num: "II",  title: "Gallery",      note: "Editorial grid — fullscreen viewing" },
  { to: "/testimonials", num: "III", title: "Testimonials", note: "Words from couples we have filmed" },
  { to: "/contact",      num: "IV",  title: "Contact",      note: "A short, quiet inquiry" },
];

export function DirectoryChapter() {
  const [firstFilm, setFirstFilm] = useState(null);

  useEffect(() => {
    let cancelled = false;
    api.get("/films")
      .then(({ data }) => {
        if (!cancelled && Array.isArray(data) && data.length > 0) setFirstFilm(data[0]);
      })
      .catch(() => { /* silent — the card simply falls back to its plain state */ });
    return () => { cancelled = true; };
  }, []);

  return (
    <section
      data-testid="directory-chapter"
      className="relative bg-transparent"
      aria-label="Studio directory"
    >
      {/* Top vignette — gentle, meets the Manifesto's bottom uniform-darken
          at the same rgba value (~0.34), so the transition into the chapter
          grid is one smooth ramp instead of a hard step. */}
      <div
        aria-hidden="true"
        className="absolute inset-x-0 top-0 h-[28vh] pointer-events-none"
        style={{
          background:
            "linear-gradient(180deg, rgba(15,12,11,0.34) 0%, rgba(15,12,11,0.20) 55%, rgba(15,12,11,0) 100%)",
        }}
      />

      {/* Chapter-wash — a gradient across the section: lighter at the top
          (blends with the Manifesto's bottom), deeper in the middle where
          the chapter grid lives, so the photograph still breathes through
          at the edges while the copy owns the frame. */}
      <div
        aria-hidden="true"
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "linear-gradient(180deg, rgba(15,12,11,0.30) 0%, rgba(15,12,11,0.62) 22%, rgba(15,12,11,0.62) 78%, rgba(15,12,11,0.30) 100%)",
        }}
      />

      {/* Bottom close-out — resolves to solid espresso so the footer's
          own #0F0C0B can meet the section with no visible seam. */}
      <div
        aria-hidden="true"
        className="absolute inset-x-0 bottom-0 h-[26vh] pointer-events-none"
        style={{
          background:
            "linear-gradient(180deg, rgba(15,12,11,0) 0%, rgba(15,12,11,0.55) 55%, rgba(15,12,11,1) 100%)",
        }}
      />

      {/* Copy — chapter grid, preserved exactly. */}
      <div className="relative z-10 px-6 md:px-12 lg:px-16 pt-32 md:pt-48 pb-32 md:pb-48">
        <Reveal>
          <p className="text-[10px] tracking-[0.42em] uppercase text-ink-champagne">
            Index of the studio
          </p>
        </Reveal>
        <div className="mt-10 md:mt-16 grid grid-cols-1 md:grid-cols-2 auto-rows-fr gap-px bg-ink-ivory/[0.08] border border-ink-ivory/[0.08]">
          {DIRECTORY.map((c, i) => {
            const isFilmsCard = c.title === "Films";
            const cover = isFilmsCard && firstFilm
              ? resolveMediaUrl(firstFilm.cover_image || firstFilm.thumbnail || "")
              : "";
            return (
              <Reveal key={c.to} delay={i * 120} className="h-full">
                <Link
                  to={c.to}
                  data-testid={`directory-link-${c.title.toLowerCase()}`}
                  className="relative flex flex-col overflow-hidden bg-[#1A1514]/85 hover:bg-[#221B1A]/90 transition-colors duration-700 p-8 md:p-12 group backdrop-blur-[2px] h-full min-h-[420px] md:min-h-[460px]"
                >
                  {isFilmsCard && cover && (
                    <>
                      <img
                        src={cover}
                        alt=""
                        aria-hidden="true"
                        className="absolute inset-0 w-full h-full object-cover opacity-[0.28] group-hover:opacity-[0.42] transition-opacity duration-1000 pointer-events-none"
                        draggable={false}
                      />
                      <div
                        aria-hidden="true"
                        className="absolute inset-0 pointer-events-none"
                        style={{
                          background:
                            "linear-gradient(115deg, rgba(15,12,11,0.85) 0%, rgba(15,12,11,0.6) 45%, rgba(15,12,11,0.35) 80%, rgba(15,12,11,0.25) 100%)",
                        }}
                      />
                    </>
                  )}
                  <div className="relative flex flex-col h-full">
                    <div className="flex items-baseline justify-between gap-6">
                      <span className="font-display text-sm tracking-[0.4em] text-ink-taupe">{c.num}</span>
                      <span className="text-[10px] tracking-[0.32em] uppercase text-ink-taupe">Chapter</span>
                    </div>
                    <h3 className="mt-10 font-display text-4xl md:text-6xl tracking-tight text-ink-ivory">
                      {c.title}
                    </h3>
                    <p className="mt-6 text-sm text-ink-ivory/70 leading-relaxed max-w-sm">{c.note}</p>
                    {isFilmsCard && firstFilm && (
                      <p
                        data-testid="directory-films-preview-title"
                        className="mt-8 text-[10px] tracking-[0.32em] uppercase text-ink-ivory/50"
                      >
                        Now playing — {firstFilm.title}
                      </p>
                    )}
                    <p className="mt-auto pt-12 text-[10px] tracking-[0.32em] uppercase text-ink-champagne editorial-link">
                      Enter →
                    </p>
                  </div>
                </Link>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
}
